import React, { useEffect, useMemo } from 'react'
import T from 'prop-types'
import getPictures from 'lib/getCarPictures'
import { Box } from '@mui/material'

import CarDetails from '../CarDetails'
import CarDetailsPageTitle from '../CarDetails/CarDetailsPageTitle'
import AssignmentCarAdditionalDetails from '../Assignment/AssignmentCarAdditionalDetails'
import { getAssignmentChoiceTotalsByPlate } from '../Assignment/assignmentLib'
import useAssignmentCarChoice from '../Assignment/useAssignmentCarChoice'

import AssignmentCarTable from './AssignmentCarTable'
import DetailsRightBottomBar from './DetailsRightBottomBar'

export default function AssignmentCarDetails({
  plate,
  assignmentType = 'nonBlind',
  session,
  readOnly,
  setShowDetails,
  cars,
  refetchList,
  contentIsReadOnly,
}) {
  const { isUpdating, fetchCarChoice, assignCar, carChoice } =
    useAssignmentCarChoice()

  useEffect(() => {
    if (!plate) {
      return
    }

    fetchCarChoice(session?.sessionId, plate, false, assignmentType)
  }, [fetchCarChoice, plate, session?.sessionId, assignmentType])

  const car = useMemo(
    () => carChoice?.assignments?.find((car) => car.plate === plate),
    [carChoice.assignments, plate]
  )

  const assignmentChoiceTotalsByPlate = useMemo(
    () => getAssignmentChoiceTotalsByPlate(cars),
    [cars]
  )

  const carWithChoiceTotals = useMemo(() => {
    if (!car) {
      return car
    }
    const totals = assignmentChoiceTotalsByPlate[car.plate] || {}
    return {
      ...car,
      firstChoicePartnersCount: totals.firstChoice ?? 0,
      blindOfferPartnersCount: totals.blindOffers ?? 0,
    }
  }, [assignmentChoiceTotalsByPlate, car])

  const pictures = useMemo(
    () => getPictures(car?.pictureurl),
    [car?.pictureurl]
  )

  const handleAssignCarChoice = async (partnerId, message) => {
    await assignCar(
      session.sessionId,
      plate,
      partnerId,
      message,
      assignmentType
    )
    await refetchList()
  }

  const handleSetShowDetails = (nextPlate) => {
    if (!nextPlate) {
      setShowDetails(false)
      return
    }

    const nextCar = cars.find((carItem) => carItem.plate === nextPlate)
    setShowDetails({
      plate: nextPlate,
      assignmentType: nextCar?.assignmentType || assignmentType,
    })
  }

  return (
    <div data-testid="car-details-container">
      <CarDetailsPageTitle
        setShowDetails={handleSetShowDetails}
        cars={cars}
        plate={plate}
      />
      {car && (
        <CarDetails
          car={carWithChoiceTotals}
          pictures={pictures}
          showAppealing
          center={
            <>
              <AssignmentCarTable
                session={session}
                carChoice={carChoice}
                handleAssignTo={handleAssignCarChoice}
                isUpdating={isUpdating}
                readOnly={
                  !!session?.assignmentEndedAt || readOnly || contentIsReadOnly
                }
              />
              <Box pb={2} />
            </>
          }
          rightBottom={
            <>
              <AssignmentCarAdditionalDetails
                vertical
                partners={carChoice?.choices}
                car={carWithChoiceTotals}
                sessionState={session.agenda.assignment.state}
              />
              <DetailsRightBottomBar plate={car?.plate} />
            </>
          }
        />
      )}
    </div>
  )
}

AssignmentCarDetails.propTypes = {
  plate: T.string.isRequired,
  assignmentType: T.string,
  session: T.object.isRequired,
  carChoice: T.object,
  assignCar: T.func,
  isUpdating: T.bool,
  readOnly: T.bool,
}
